import { createSlice } from '@reduxjs/toolkit';

// ---------------------------------------------------------------------------
// Storage helpers
// "Remember me" users are kept in localStorage, everyone else in
// sessionStorage. Whichever one currently holds the session is the one we
// keep writing to — so updating the profile never moves or deletes the token.
// ---------------------------------------------------------------------------

function parseStored(value) {
  if (!value) return null;
  try {
    return typeof value === 'string' ? JSON.parse(value) : value;
  } catch (e) {
    return null;
  }
}

function currentStorageMode() {
  if (localStorage.getItem('token') || localStorage.getItem('userInfo')) return 'local';
  return 'session';
}

function writeSession(mode, userInfo, token, refreshToken) {
  const primary = mode === 'local' ? localStorage : sessionStorage;
  const other = mode === 'local' ? sessionStorage : localStorage;

  if (userInfo) primary.setItem('userInfo', JSON.stringify(userInfo));
  if (token) primary.setItem('token', token);
  if (refreshToken) primary.setItem('refreshToken', refreshToken);

  other.removeItem('userInfo');
  other.removeItem('token');
  other.removeItem('refreshToken');
}

// Helper to load initial state
const loadInitialState = () => {
  // localStorage first ("remember me" users)
  const localUser = localStorage.getItem('userInfo');
  if (localUser) {
    return {
      userInfo: parseStored(localUser),
      token: localStorage.getItem('token'),
      refreshToken: localStorage.getItem('refreshToken'),
    };
  }

  const sessionUser = sessionStorage.getItem('userInfo');
  if (sessionUser) {
    return {
      userInfo: parseStored(sessionUser),
      token: sessionStorage.getItem('token'),
      refreshToken: sessionStorage.getItem('refreshToken'),
    };
  }

  return { userInfo: null, token: null, refreshToken: null };
};

const initialState = loadInitialState();

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // payload: { userInfo, token?, refreshToken?, rememberMe? }
    // - token missing  -> the current token is KEPT (profile / plan updates
    //   often dispatch setUser without it, which used to log the user out)
    // - rememberMe missing -> stays in whichever storage the session is in now
    //   (login should pass rememberMe: true/false explicitly)
    setUser(state, action) {
      const { userInfo, token, refreshToken, rememberMe } = action.payload || {};

      state.userInfo = userInfo;
      state.token = token || state.token;
      state.refreshToken = refreshToken || state.refreshToken;

      const mode =
        rememberMe === true
          ? 'local'
          : rememberMe === false
          ? 'session'
          : currentStorageMode();

      writeSession(mode, state.userInfo, state.token, state.refreshToken);
    },

    // Merges partial fields into userInfo (e.g. { family_planning_goal: 'ivf_journey' })
    // without touching the token or the storage mode.
    updateUserInfo(state, action) {
      if (!state.userInfo) return;
      state.userInfo = { ...state.userInfo, ...action.payload };
      writeSession(currentStorageMode(), state.userInfo, state.token, state.refreshToken);
    },

    clearUser(state) {
      state.userInfo = null;
      state.token = null;
      state.refreshToken = null;
      localStorage.removeItem('userInfo');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      sessionStorage.removeItem('userInfo');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
    },
  },
});

export const { setUser, updateUserInfo, clearUser } = userSlice.actions;
export default userSlice.reducer;